import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { CheckCircle2, AlertCircle, Lock } from 'lucide-react';

export const MagicLinkPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [state, setState] = useState({ loading: true, user: null, error: null });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const user = await api.verifyMagicLink(token);
        setState({ loading: false, user, error: null });
      } catch (err) {
        setState({ loading: false, user: null, error: err.response?.data?.detail || "Lien invalide" });
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("8 caractères minimum"); return; }
    if (password !== confirm) { toast.error("Les mots de passe ne correspondent pas"); return; }
    setSubmitting(true);
    try {
      await api.setPasswordViaMagicLink(token, password);
      toast.success("Mot de passe enregistré !");
      try { await login(state.user.email, password); } catch (_) {}
      navigate('/fr/mon-dossier');
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setSubmitting(false); }
  };

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF7F0]">
        <div className="animate-spin w-8 h-8 border-2 border-[#C9B896] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen bg-[#FAF7F0] flex items-center justify-center px-6" data-testid="magic-link-error">
        <div className="max-w-md text-center bg-white border border-[#1F2A4A]/10 rounded-2xl p-10 shadow-sm">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="font-serif text-2xl text-[#1F2A4A] mb-3">Lien invalide ou expiré</h1>
          <p className="text-sm text-[#1F2A4A]/60 mb-6">{state.error}</p>
          <Button onClick={() => navigate('/fr/contact')} className="bg-[#C9B896] text-[#1F2A4A] rounded-full">
            Contacter l'école
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF7F0] flex items-center justify-center px-6 py-20" data-testid="magic-link-page">
      <div className="w-full max-w-md bg-white border border-[#1F2A4A]/10 rounded-2xl p-10 shadow-sm">
        <p className="text-xs font-mono uppercase tracking-[0.3em] text-[#C9B896] mb-3">FINALISATION DE COMPTE</p>
        <h1 className="font-serif text-3xl text-[#1F2A4A] font-light leading-tight mb-3">
          Bienvenue {state.user.first_name},
        </h1>
        <p className="text-sm text-[#1F2A4A]/70 leading-relaxed mb-8">
          Définis ton mot de passe pour accéder à ton espace élève et compléter ton dossier de candidature.
        </p>
        <form onSubmit={submit} className="space-y-5">
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#C9B896]">Email</Label>
            <Input value={state.user.email} disabled className="mt-1 bg-[#FAF7F0]/40" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#C9B896]">Nouveau mot de passe</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1" autoFocus data-testid="magic-password" />
            <p className="text-[10px] text-[#1F2A4A]/40 mt-1">8 caractères minimum</p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#C9B896]">Confirmer le mot de passe</Label>
            <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1" data-testid="magic-password-confirm" />
          </div>
          <Button type="submit" disabled={submitting} className="w-full bg-[#1F2A4A] text-[#FAF7F0] hover:bg-[#324166] rounded-full py-6" data-testid="magic-submit">
            <Lock className="w-4 h-4 mr-2" /> {submitting ? 'Enregistrement…' : 'Définir mon mot de passe'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default MagicLinkPage;
